// =============================================================
// src/lib/socket.js
// =============================================================
import { io } from 'socket.io-client';

// 可用 localStorage 開啟除錯：localStorage.DEBUG_SOCKET = '1';
const DEBUG = typeof window !== 'undefined' && window.localStorage?.getItem('DEBUG_SOCKET') === '1';

// 與同網域後端連線（server 已用 path:'/socket.io'）
const socket = io('/', {
  path: '/socket.io',
  transports: ['websocket', 'polling'],
  reconnection: true,
  reconnectionAttempts: Infinity,
  reconnectionDelay: 500,
  reconnectionDelayMax: 5000,
  timeout: 10000,
});

// ---- 小工具：等待連上才開始做事 ----
let _connectedOnce = false;
let _connectedWaiters = [];
function _resolveAll() {
  _connectedOnce = true;
  _connectedWaiters.forEach((r) => r());
  _connectedWaiters = [];
}

// 等待 socket 真正 connected（回傳 Promise）
export function waitUntilConnected() {
  if (_connectedOnce && socket.connected) return Promise.resolve();
  return new Promise((resolve) => {
    _connectedWaiters.push(resolve);
    // 若已 connected，立即 resolve
    if (socket.connected) _resolveAll();
  });
}

// ---- 訂閱／退訂幫手：百萬大歌星（沿用後端事件名）----
// 事件清單：
// 'singing_game:state'      -> { round, picks, score, pickedSongIds, categories?, songs? }（依後端實作）
// 'singing_game:category'   -> { categoryId, categoryName, songs[], pickedSongIds[] }
// 'singing_game:play'       -> { roundId, pickId, song:{ id,title,audioUrl,bgUrl,passAnimUrl,failAnimUrl } }
// 'singing_game:mask'       -> { pickId, masked }   // ******_____ 即時遮罩
// 'singing_game:result'     -> { pickId, result:'pass'|'fail', answer, score }

export function onSingingGame(event, handler) {
  socket.on(event, handler);
  if (DEBUG) console.log('[socket] on:', event);
  // 回傳取消訂閱函式
  return () => {
    socket.off(event, handler);
    if (DEBUG) console.log('[socket] off:', event);
  };
}

// 批次訂閱：傳一個事件名到處理函式的 map
export function subscribeSingingGame(handlers = {}) {
  const offs = Object.entries(handlers).map(([evt, fn]) => onSingingGame(evt, fn));
  return () => offs.forEach((off) => off());
}

// ---- 常見事件（留言牆、九宮格）也順手包一下 ----
export function onWallMessage(handler) {
  return onSingingGame('msg', handler); // 後端有 io.emit('msg', s)
}
export function onGameState(handler) {
  return onSingingGame('game:state', handler);
}

// ---- 連線狀態日誌（可關閉）----
socket.on('connect', () => {
  if (DEBUG) console.log('[socket] connected', socket.id);
  _resolveAll();
});
socket.on('disconnect', (reason) => {
  if (DEBUG) console.log('[socket] disconnected:', reason);
});
socket.on('reconnect', (attempt) => {
  if (DEBUG) console.log('[socket] reconnected, attempt:', attempt);
});
socket.on('reconnect_attempt', (attempt) => {
  if (DEBUG) console.log('[socket] reconnect_attempt:', attempt);
});
socket.on('connect_error', (err) => {
  if (DEBUG) console.warn('[socket] connect_error:', err?.message || err);
});

// ---- 直接輸出 socket（維持你原本的用法相容）----
export default socket;
